import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import Button from '../ui/Button';
import xeroPayrollService, { type XeroTilAccrual } from '../../services/xeroPayroll';
import { formatLocalDateInTz } from '../../utils/timezone';

/**
 * Banked time in lieu on its way to Xero (2026-10-08).
 *
 * Xero cannot accrue time in lieu from a timesheet, so once a timesheet with
 * banked hours is posted, the hours wait here until payroll creates the draft
 * pay run, and are then written onto the employee's payslip as a Time in Lieu
 * accrual. That happens on every pay-run check (every 30 minutes in the day)
 * and from the button. A pay run Xero posted first cannot take them; those are
 * listed for payroll to add in Xero by hand.
 */
export default function XeroTilAccruals({ canAccrue, orgTz }: { canAccrue: boolean; orgTz: string }) {
  const [rows, setRows] = useState<XeroTilAccrual[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await xeroPayrollService.listTilAccruals();
      if (res.success && res.data) setRows(res.data);
    } catch (e) {
      setError((e as Error)?.message || 'Could not read the time in lieu waiting for Xero.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const sendNow = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await xeroPayrollService.sendTilAccruals();
      if (res.success && res.data) {
        setRows(res.data.accruals);
        const { sent, missed, waiting } = res.data;
        setInfo([
          sent ? `${sent} added to draft payslips` : null,
          missed ? `${missed} too late (pay run already posted)` : null,
          waiting ? `${waiting} still waiting for a draft pay run` : null,
        ].filter(Boolean).join(', ') || 'Nothing was waiting.');
      }
    } catch (e) {
      setError((e as Error)?.message || 'Could not send time in lieu to Xero.');
    } finally {
      setBusy(false);
    }
  };

  const open = (rows ?? []).filter(r => r.status !== 'sent');
  const recentSent = (rows ?? []).filter(r => r.status === 'sent').slice(0, 5);
  const name = (r: XeroTilAccrual) => r.employee_name || r.username;
  const range = (r: XeroTilAccrual) => `${formatLocalDateInTz(r.start_date, orgTz)} – ${formatLocalDateInTz(r.end_date, orgTz)}`;

  return (
    <div className="p-3 border border-neutral-200 rounded-lg space-y-3 text-sm">
      <div>
        <p className="font-medium text-neutral-900">Time in lieu banked here, added to Xero</p>
        <p className="text-neutral-600 mt-1">
          Xero cannot add banked time in lieu from a timesheet. Once payroll creates the draft pay run in Xero,
          each person's banked hours are added to their payslip as a Time in Lieu accrual. Create the draft pay run,
          then press Add, before posting it.
        </p>
      </div>

      {!canAccrue && (
        <p className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-900">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span className="min-w-0">
            Xero has not allowed FactreeTech to change payslips yet. Reconnect Xero at the top of this page and
            approve the new permission; until then banked hours wait here.
          </span>
        </p>
      )}

      {open.length > 0 ? (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded">
          {open.map(r => (
            <li key={r.id} className="p-2">
              <p className="text-neutral-900 break-words">
                <strong>{Number(r.hours).toFixed(2)} h</strong> · {name(r)} · {range(r)}
              </p>
              <p className={`text-xs mt-0.5 break-words ${r.status === 'missed' ? 'text-red-700' : 'text-neutral-500'}`}>
                {r.status === 'missed'
                  ? (r.error || 'Xero posted the pay run first. Add these hours to their Time in Lieu balance in Xero.')
                  : (r.error || 'Waiting for the draft pay run in Xero.')}
              </p>
            </li>
          ))}
        </ul>
      ) : rows !== null && (
        <p className="text-neutral-500">Nothing waiting.</p>
      )}

      {recentSent.length > 0 && (
        <p className="text-xs text-neutral-500 break-words">
          Recently added: {recentSent.map(r => `${name(r)} ${Number(r.hours).toFixed(2)} h`).join(', ')}.
        </p>
      )}

      {error && <p className="text-red-600 break-words">{error}</p>}
      {info && <p className="text-emerald-700 break-words">{info}</p>}

      <Button size="sm" variant="outline" onClick={() => void sendNow()} disabled={busy || !canAccrue} className="flex items-center gap-1">
        <RefreshCw className="h-4 w-4" />
        {busy ? 'Adding…' : 'Add to draft pay runs now'}
      </Button>
    </div>
  );
}
